// ============================================================================
// Facebook Messenger — user profile lookup.
//
// Given a PSID (page-scoped user id), fetch the subset of profile fields our
// messaging capability exposes. Facebook only returns fields a page is
// authorized to see; missing fields (e.g. `profile_pic`) are normal and the
// mapper degrades gracefully.
//
// The returned `UserProfile` is the capability-level shape (see
// `../../capabilities/messaging.ts`) — callers should not depend on the raw
// Graph API response format.
// ============================================================================

import type { FetchUserProfileArgs, UserProfile } from '../../capabilities/messaging'
import {
  classifyFacebookError,
  FacebookMessengerError,
  parseRetryAfterMs,
  type FacebookErrorEnvelope,
  type FacebookUserProfile,
} from './types'

// ============================================================================
// Constants
// ============================================================================

const GRAPH_API_BASE = 'https://graph.facebook.com/v25.0'
const PROFILE_FIELDS = 'first_name,last_name,profile_pic,locale'
const CONVERSATION_FIELDS = 'id,participants'

type FacebookFetchUserProfileArgs = FetchUserProfileArgs & {
  /**
   * Messenger profile lookup by PSID can be denied by Meta for some inbox
   * participants even when the Page token is valid. When pageId is available,
   * we can fall back to the Page Conversations edge and read the non-page
   * participant display name.
   */
  pageId?: string
}

interface FacebookConversationParticipant {
  id?: string
  name?: string
}

interface FacebookConversationList {
  data?: Array<{
    participants?: {
      data?: FacebookConversationParticipant[]
    }
  }>
}

// ============================================================================
// fetchUserProfile
// ============================================================================

export async function fetchUserProfile(
  args: FacebookFetchUserProfileArgs,
  accessToken: string,
  fetchImpl: typeof fetch = fetch,
): Promise<UserProfile> {
  validateUserId(args.userId)
  assertAccessToken(accessToken)

  try {
    return await fetchUserProfileDirect(args, accessToken, fetchImpl)
  } catch (err) {
    if (!shouldTryConversationFallback(err, args.pageId)) {
      throw err
    }
    return fetchUserProfileFromConversation(
      args.userId,
      args.pageId!,
      accessToken,
      fetchImpl,
    )
  }
}

async function fetchUserProfileDirect(
  args: FetchUserProfileArgs,
  accessToken: string,
  fetchImpl: typeof fetch,
): Promise<UserProfile> {
  // Access token goes in the Authorization header — not the URL — so
  // Cloudflare edge / proxy logs never capture it. `encodeURIComponent` on
  // the PSID blocks path traversal tricks (FB PSIDs are numeric strings so
  // the encoding is a no-op in practice but belt-and-braces for future ids).
  const url = new URL(`${GRAPH_API_BASE}/${encodeURIComponent(args.userId)}`)
  url.searchParams.set('fields', PROFILE_FIELDS)

  const response = await fetchImpl(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    throw await errorFromResponse(response)
  }

  const raw = (await response.json()) as FacebookUserProfile
  return mapProfile(args.userId, raw)
}

async function fetchUserProfileFromConversation(
  userId: string,
  pageId: string,
  accessToken: string,
  fetchImpl: typeof fetch,
): Promise<UserProfile> {
  const url = new URL(
    `${GRAPH_API_BASE}/${encodeURIComponent(pageId)}/conversations`,
  )
  url.searchParams.set('user_id', userId)
  url.searchParams.set('fields', CONVERSATION_FIELDS)
  url.searchParams.set('limit', '1')

  const response = await fetchImpl(url.toString(), {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    throw await errorFromResponse(response)
  }

  const raw = (await response.json()) as FacebookConversationList
  const participants = raw.data?.[0]?.participants?.data ?? []
  const customer = participants.find((p) => p.id && p.id !== pageId) ?? participants[0]
  const name = typeof customer?.name === 'string' ? customer.name.trim() : ''

  if (!name) {
    throw new FacebookMessengerError(
      'API_ERROR',
      'Facebook conversation participant did not include a display name',
      { httpStatus: response.status },
    )
  }

  return {
    id: userId,
    name,
    extra: {
      id: userId,
      pageId,
      participantId: customer?.id,
      profileSource: 'page_conversations',
    },
  }
}

// ============================================================================
// Mapping — FB profile → capability `UserProfile`
// ============================================================================

function mapProfile(userId: string, raw: FacebookUserProfile): UserProfile {
  const firstName = (raw.first_name ?? '').trim()
  const lastName = (raw.last_name ?? '').trim()
  const combined = `${firstName} ${lastName}`.trim()

  // Prefer full name, else first name, else fall back to the PSID so the UI
  // always has SOMETHING to render.
  const name = combined.length > 0
    ? combined
    : firstName.length > 0
      ? firstName
      : userId

  const profile: UserProfile = {
    id: userId,
    name,
    avatarUrl: raw.profile_pic,
    locale: raw.locale,
    extra: {
      id: userId,
      firstName: firstName.length > 0 ? firstName : undefined,
      lastName: lastName.length > 0 ? lastName : undefined,
    },
  }
  return profile
}

function shouldTryConversationFallback(
  err: unknown,
  pageId: string | undefined,
): boolean {
  return Boolean(
    pageId &&
      err instanceof FacebookMessengerError &&
      err.code === 'API_ERROR',
  )
}

// ============================================================================
// Internal helpers
// ============================================================================

async function errorFromResponse(response: Response): Promise<FacebookMessengerError> {
  let envelope: FacebookErrorEnvelope | null = null
  try {
    envelope = (await response.json()) as FacebookErrorEnvelope
  } catch {
    // Non-JSON error body (edge gateway HTML etc.) — leave envelope null.
  }
  const retryAfterMs = parseRetryAfterMs(response.headers.get('Retry-After'))
  return classifyFacebookError(envelope, response.status, retryAfterMs)
}

function validateUserId(userId: string | undefined | null): void {
  if (!userId || typeof userId !== 'string' || userId.trim().length === 0) {
    throw new FacebookMessengerError(
      'INVALID_INPUT',
      'fetchUserProfile: userId is required',
    )
  }
}

function assertAccessToken(accessToken: string | undefined | null): void {
  if (!accessToken || typeof accessToken !== 'string') {
    throw new FacebookMessengerError('INVALID_INPUT', 'accessToken is required')
  }
}
